import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";

import getBusinessNetworkTableRows from "../helpers/getBusinessNetworkTableRows";

const ui = {
  border: "#e6e9ee",
  text: "#182230",
  muted: "#667085",
  subtle: "#98a2b3",
  soft: "#f8fafc",
};

const BusinessNetworkTable = ({
  customerCards,
  handleContextMenu,
  treeData,
}) => {
  const intl = useIntl();
  const [search, setSearch] = useState("");
  const [networkFilter, setNetworkFilter] = useState("");

  const rows = useMemo(
    () => getBusinessNetworkTableRows(treeData),
    [treeData]
  );

  const networkNames = useMemo(
    () =>
      [...new Set(rows.map(row => row.rootNetworkName).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b, "sv")),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("sv");

    return rows.filter(row => {
      if (
        networkFilter &&
        row.rootNetworkName !== networkFilter
      ) {
        return false;
      }

      if (!query) {
        return true;
      }

      const customerCard =
        row.type === "company"
          ? customerCards?.[row.companyId] || null
          : null;

      return [
        row.name,
        row.type === "network" ? "nätverk" : "organisation företag",
        row.rootNetworkName,
        row.parentName,
        row.networkId,
        row.companyId,
        row.memberId,
        customerCard?.customerNumber,
        customerCard?.organizationNumber,
        customerCard?.vatNumber,
      ]
        .filter(value => value !== null && value !== undefined)
        .join(" ")
        .toLocaleLowerCase("sv")
        .includes(query);
    });
  }, [customerCards, networkFilter, rows, search]);

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={0.8}
        alignItems={{ xs: "stretch", md: "center" }}
        sx={{
          px: 1.25,
          py: 1.05,
          borderBottom: `1px solid ${ui.border}`,
          bgcolor: "#fff",
        }}
      >
        <TextField
          size="small"
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder={intl.formatMessage({
            id: "admin.crm.businessNetworks.table.search",
            defaultMessage: "Sök organisation, nätverk eller ID...",
          })}
          sx={{
            flex: 1,
            minWidth: { xs: 0, md: 260 },
            "& .MuiOutlinedInput-root": {
              height: 36,
              borderRadius: 1,
              fontSize: 12,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon sx={{ fontSize: 17, color: ui.subtle }} />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          select
          size="small"
          value={networkFilter}
          onChange={event => setNetworkFilter(event.target.value)}
          sx={{
            minWidth: { xs: "100%", md: 220 },
            "& .MuiOutlinedInput-root": {
              height: 36,
              borderRadius: 1,
              fontSize: 12,
            },
          }}
        >
          <MenuItem value="">
            {intl.formatMessage({
              id: "admin.crm.businessNetworks.table.allNetworks",
              defaultMessage: "Alla affärsnätverk",
            })}
          </MenuItem>

          {networkNames.map(name => (
            <MenuItem key={name} value={name}>
              {name}
            </MenuItem>
          ))}
        </TextField>

        <Typography
          sx={{
            ml: { md: "auto" },
            color: ui.muted,
            fontSize: 10.5,
            whiteSpace: "nowrap",
          }}
        >
          {intl.formatMessage(
            {
              id: "admin.crm.businessNetworks.table.resultCountRows",
              defaultMessage: "{count} poster",
            },
            { count: filteredRows.length }
          )}
        </Typography>
      </Stack>

      <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table
          size="small"
          stickyHeader
          sx={{
            minWidth: 980,
            "& .MuiTableCell-root": {
              borderColor: ui.border,
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, width: "30%" }}>
                {intl.formatMessage({
                  id: "admin.crm.businessNetworks.table.name",
                  defaultMessage: "Namn",
                })}
              </TableCell>

              <TableCell sx={{ ...headCellSx, width: 110 }}>
                {intl.formatMessage({
                  id: "admin.crm.businessNetworks.table.type",
                  defaultMessage: "Typ",
                })}
              </TableCell>

              <TableCell sx={headCellSx}>
                {intl.formatMessage({
                  id: "admin.crm.businessNetworks.table.network",
                  defaultMessage: "Affärsnätverk",
                })}
              </TableCell>

              <TableCell sx={headCellSx}>
                {intl.formatMessage({
                  id: "admin.crm.businessNetworks.table.belongsTo",
                  defaultMessage: "Tillhör",
                })}
              </TableCell>

              <TableCell sx={{ ...headCellSx, width: 70 }}>
                {intl.formatMessage({
                  id: "admin.crm.businessNetworks.table.level",
                  defaultMessage: "Nivå",
                })}
              </TableCell>

              <TableCell sx={{ ...headCellSx, width: 92 }}>
                {intl.formatMessage({
                  id: "admin.crm.businessNetworks.table.children",
                  defaultMessage: "Underliggande",
                })}
              </TableCell>

              <TableCell sx={{ ...headCellSx, width: 110 }}>
                {intl.formatMessage({
                  id: "admin.crm.businessNetworks.table.customerNumber",
                  defaultMessage: "Kundnr",
                })}
              </TableCell>

              <TableCell sx={{ ...headCellSx, width: 105 }}>
                {intl.formatMessage({
                  id: "admin.crm.businessNetworks.table.customerCard",
                  defaultMessage: "Kundkort",
                })}
              </TableCell>

              <TableCell sx={{ ...headCellSx, width: 95 }}>
                {intl.formatMessage({
                  id: "admin.crm.businessNetworks.table.id",
                  defaultMessage: "ID",
                })}
              </TableCell>

              <TableCell sx={{ ...headCellSx, width: 54 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} sx={{ py: 4, textAlign: "center" }}>
                  <Typography sx={{ color: ui.text, fontSize: 12.5, fontWeight: 520 }}>
                    {intl.formatMessage({
                      id: "admin.crm.businessNetworks.table.noResults",
                      defaultMessage: "Inga poster matchar filtret.",
                    })}
                  </Typography>
                  <Typography sx={{ mt: 0.35, color: ui.muted, fontSize: 10.5 }}>
                    {intl.formatMessage({
                      id: "admin.crm.businessNetworks.table.noResultsHint",
                      defaultMessage: "Ändra sökning eller välj ett annat affärsnätverk.",
                    })}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {filteredRows.map(row => {
              const isNetwork = row.type === "network";
              const isRoot = isNetwork && row.level === 1;

              const customerCard = !isNetwork
                ? customerCards?.[row.companyId] || null
                : null;

              return (
                <TableRow
                  key={row.key}
                  hover
                  onContextMenu={event =>
                    handleContextMenu(event, row.node)
                  }
                  sx={{
                    bgcolor: isRoot ? "#fbfcfd" : "#fff",
                    cursor: "default",
                  }}
                >
                  <TableCell sx={{ py: 0.7 }}>
                    <Stack
                      direction="row"
                      spacing={0.8}
                      alignItems="center"
                      sx={{
                        pl: `${Math.max(0, row.level - 1) * 18}px`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 27,
                          height: 27,
                          borderRadius: 1,
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                          color: isNetwork ? "#536579" : "primary.main",
                          bgcolor: isNetwork
                            ? "#f3f5f7"
                            : theme => `${theme.palette.primary.main}0D`,
                        }}
                      >
                        {isNetwork ? (
                          <AccountTreeOutlinedIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ApartmentOutlinedIcon sx={{ fontSize: 16 }} />
                        )}
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          noWrap
                          sx={{
                            color: ui.text,
                            fontSize: 11.5,
                            fontWeight: isRoot ? 600 : 520,
                          }}
                        >
                          {row.name}
                        </Typography>

                      </Box>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <TypeChip type={row.type} />
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    {row.rootNetworkName}
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    {row.parentName}
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      label={row.level}
                      sx={{
                        height: 20,
                        minWidth: 28,
                        borderRadius: 1,
                        bgcolor: ui.soft,
                        color: ui.muted,
                        border: `1px solid ${ui.border}`,
                        fontSize: 9.6,
                        fontWeight: 520,
                        "& .MuiChip-label": { px: 0.65 },
                      }}
                    />
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    {row.childCount}
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    {customerCard?.customerNumber || "—"}
                  </TableCell>

                  <TableCell>
                    {isNetwork ? (
                      <Typography sx={{ color: ui.subtle, fontSize: 10 }}>
                        —
                      </Typography>
                    ) : (
                      <CustomerCardChip exists={Boolean(customerCard)} />
                    )}
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    {row.idLabel}
                  </TableCell>

                  <TableCell align="right">
                    <Tooltip
                      title={intl.formatMessage({
                        id: "common.actions",
                        defaultMessage: "Åtgärder",
                      })}
                    >
                      <IconButton
                        size="small"
                        onClick={event => handleContextMenu(event, row.node)}
                      >
                        <MoreVertOutlinedIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const CustomerCardChip = ({ exists }) => (
  <Chip
    size="small"
    label={exists ? "Skapat" : "Saknas"}
    sx={{
      height: 20,
      borderRadius: 1,
      bgcolor: exists ? "#eef8f2" : "#fff5dc",
      color: exists ? "#247a4d" : "#8a5a00",
      border: `1px solid ${exists ? "#cce8d7" : "#f0d89a"}`,
      fontSize: 9.3,
      fontWeight: 520,
      "& .MuiChip-label": { px: 0.7 },
    }}
  />
);

const TypeChip = ({ type }) => {
  const isNetwork = type === "network";

  return (
    <Chip
      size="small"
      label={isNetwork ? "Nätverk" : "Organisation"}
      sx={{
        height: 20,
        borderRadius: 1,
        bgcolor: isNetwork ? "#f3f5f7" : "#f8f3f6",
        color: isNetwork ? "#536579" : "#6e2341",
        border: `1px solid ${isNetwork ? "#dfe4e9" : "#eadce3"}`,
        fontSize: 9.4,
        fontWeight: 520,
        "& .MuiChip-label": { px: 0.7 },
      }}
    />
  );
};

const headCellSx = {
  py: 0.9,
  bgcolor: "#fbfcfd",
  color: ui.muted,
  fontSize: 10,
  fontWeight: 550,
};

const bodyCellSx = {
  py: 0.72,
  color: ui.text,
  fontSize: 10.6,
};

BusinessNetworkTable.defaultProps = {
  customerCards: {},
};

export default BusinessNetworkTable;
